import Button from "@/components/button/Button"
import { useAuth } from "@/components/context/AuthProvider"
import { useGeneralContext } from "@/components/context/GeneralContext"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import { SvgIcon } from "@/components/icon"
import { ApplyItemType } from "@/components/modal-dark/modal-dark-platform-content/single-submission/SingleSubmission"
import HexagonGroup from "@/components/modal-dark/modal-dark-platform-content/start-notice/hexagon-group/HexagonGroup"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { useApplicationQuery } from "@/graphql"
import { Col, Row, Skeleton } from "antd"
import { FC } from "react"
import { useNavigate } from "react-router"
import "./AutoApprovalSuccess.less"
import applyImage03 from "./assets/intersection.svg"
import applyImage01 from "./assets/list.svg"
import applyImage02 from "./assets/mark.svg"

const { Title, Paragraph } = WebsiteTypography

const AutoApprovalSuccess: FC = () => {
  const navigate = useNavigate()
  const { currentApplicationId } = useGeneralContext()
  const { organization } = useAuth()
  const { setIsModalDarkOpen } = useModalDarkContext()
  const { data, loading } = useApplicationQuery({
    variables: { id: currentApplicationId || "" },
    skip: !currentApplicationId,
  })

  const appData = data?.application?.data as ApplicationEntity
  const applicationNumber = appData?.attributes?.title || ""

  const hasAffectedStatus = !!appData?.attributes?.approvals?.data?.some(it => it?.attributes?.status !== "approved")

  const applyItems: ApplyItemType[] = [
    {
      id: "1",
      description: "Your project details and documents successfully validated.",
      imageUrl: applyImage01,
    },
    {
      id: "2",
      description: "Your project intersects one of our service areas.",
      imageUrl: applyImage02,
    },
    {
      id: "3",
      description: hasAffectedStatus
        ? "Your project impacts our existing utility network."
        : "Your project does not impact existing utility network.",
      imageUrl: applyImage03,
    },
  ]

  const handleSeeApplication = () => {
    navigate(`/${organization}/dashboard/application/overview/?id=${appData?.attributes?.uuid}`)
    setIsModalDarkOpen(false)
  }

  const handleClose = () => {
    setIsModalDarkOpen(false)
  }

  if (loading) {
    return <Skeleton paragraph={{ rows: 5 }} />
  }
  return (
    <Row justify={"center"} className={"auto-approval-success "}>
      <Col span={20} xxl={20} xl={20} lg={20} md={24} sm={24} xs={24}>
        <Paragraph centered color={"white"} weight={"w300"} size={"lg"} style={{ paddingBottom: 16 }}>
          {"Quick Approval Report"}
        </Paragraph>

        {!hasAffectedStatus && (
          <>
            <Title level={4} centered color={"green"} style={{ marginTop: 0 }}>
              <span className={"w300"}>{"Services".toUpperCase()}</span>
              <span className={"bold"}>{" Not Affected".toUpperCase()}</span>
            </Title>

            <Paragraph size={"lg"} weight={"w300"} centered style={{ marginTop: 0, paddingBottom: 24 }}>
              {"Application Automatically Approved"}
            </Paragraph>
          </>
        )}

        {hasAffectedStatus && (
          <>
            <Title level={3} centered className={"services-affected"} style={{ marginTop: 0 }}>
              {"Services Affected"}
            </Title>

            <Paragraph size={"lg"} centered style={{ marginTop: 0, paddingBottom: 24 }}>
              {"DO NOT DIG - Further review required!"}
            </Paragraph>
          </>
        )}
      </Col>

      <Row justify={"center"}>
        <div className={"info-steps-wrapper"}>
          <div className={"how-to-start-wrapper"}>
            {applyItems?.map((it, index) => {
              const isAffected = index === applyItems.length - 1 && hasAffectedStatus
              return (
                <div className={"step-wrapper"} key={it?.id}>
                  <HexagonGroup id={it?.id} imageUrl={it?.imageUrl} isTicked isExclamation={isAffected} />
                  <div className={"text-wrapper"}>
                    <Paragraph
                      centered
                      color={"website-secondary"}
                      size={"sm"}
                      weight={"w300"}
                      className={isAffected ? "affected" : ""}
                    >
                      {it?.description}
                    </Paragraph>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Row>

      <Col span={18} style={{ paddingTop: 32, paddingBottom: 24 }}>
        <Row justify={"center"}>
          <div className={"support-btn"}>
            <div className={"support-wrapper"}>
              <div className={"hexagon-wrapper"}>
                <SvgIcon type={"green-tick-in-circle"} />
              </div>
              <div>
                <Paragraph color={"website-secondary"}>
                  {hasAffectedStatus
                    ? `Application ${applicationNumber} has been pre-approved.`
                    : `Application ${applicationNumber} has been approved successfully.`}
                </Paragraph>
                <Paragraph color={"website-secondary"} size={"xs"} style={{ paddingTop: 8 }}>
                  {hasAffectedStatus
                    ? "Your project affects our existing services. A detailed service impact assessment will be done and sent to you. No work must start until this assessment has been shared and a site meeting has been conducted."
                    : "A confirmation email with the approval document has been sent to your contact email."}
                </Paragraph>
              </div>
            </div>
          </div>
        </Row>
      </Col>

      <Row justify={"center"} className={"help-paragraph"}>
        <Col span={14}>
          <Paragraph color={"website-secondary"} size={"xs"} centered>
            {
              "For more details, check your application by clicking the button below, or access it anytime through your personal dashboard."
            }
          </Paragraph>
        </Col>
      </Row>

      <Col span={14} style={{ paddingTop: 40 }}>
        <Row align={"middle"} justify={"center"} gutter={8}>
          <Col span={12}>
            <Button btnType={"ghost"} text={"Close"} textColor={"white"} size={"large"} onClick={handleClose} block />
          </Col>

          <Col span={12}>
            <Button btnType={"primary"} text={"View Application"} size={"large"} onClick={handleSeeApplication} block />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default AutoApprovalSuccess
